import { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAdminAuth } from '@/context/AdminAuthContext';
import { useToast } from '@/context/ToastContext';

const NAV_ITEMS = [
    {
        label: 'Dashboard',
        href: '/adminpanel/dashboard',
        icon: (
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8}
                    d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
            </svg>
        ),
    },
    {
        label: 'Upload Model',
        href: '/adminpanel/models/upload',
        icon: (
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8}
                    d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
            </svg>
        ),
    },
    {
        label: 'Model List',
        href: '/adminpanel/models/list',
        icon: (
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8}
                    d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
        ),
    },
    {
        label: 'Menus',
        href: '/adminpanel/menus',
        icon: (
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8}
                    d="M4 6h16M4 12h16M4 18h7" />
            </svg>
        ),
    },
    {
        label: 'Users',
        href: '/adminpanel/users',
        icon: (
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8}
                    d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
        ),
        adminOnly: true,
    },
];

export default function AdminLayout() {
    const [sidebarOpen, setSidebarOpen] = useState(true);
    const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
    const { user, logout } = useAdminAuth();
    const toast = useToast();
    const navigate = useNavigate();
    const location = useLocation();

    // Close mobile sidebar on route change
    useEffect(() => { setMobileSidebarOpen(false); }, [location.pathname]);

    const handleLogout = async () => {
        await logout();
        toast.success('Logged out successfully.');
        navigate('/adminpanel/login');
    };

    const avatarInitials = user?.name
        ? user.name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2)
        : 'AD';

    return (
        <div className="min-h-screen flex bg-[#060d1f] text-slate-200 font-sans">

            {/* ── Mobile overlay ──────────────────────────────────── */}
            {mobileSidebarOpen && (
                <div
                    className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 lg:hidden"
                    onClick={() => setMobileSidebarOpen(false)}
                />
            )}

            {/* ── Sidebar ─────────────────────────────────────────── */}
            <aside
                className={`
                    fixed top-0 left-0 h-full z-50 flex flex-col
                    transition-all duration-300 ease-in-out
                    bg-gradient-to-b from-[#0a1628] to-[#060d1f]
                    border-r border-white/5
                    ${sidebarOpen ? 'w-64' : 'w-[72px]'}
                    ${mobileSidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
                `}
            >
                {/* Logo */}
                <div className="flex items-center gap-3 px-4 py-5 border-b border-white/5">
                    <div className="flex-shrink-0 w-9 h-9 rounded-xl bg-gradient-to-br from-accent-500 to-primary-600 flex items-center justify-center shadow-lg">
                        <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                    </div>
                    {sidebarOpen && (
                        <div className="overflow-hidden">
                            <p className="text-sm font-bold text-white tracking-wide">VisionAI</p>
                            <p className="text-[10px] text-accent-400 font-medium tracking-widest uppercase">Admin Panel</p>
                        </div>
                    )}
                </div>

                {/* Nav items */}
                <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1">
                    {NAV_ITEMS.filter(item => !item.adminOnly || user?.role === 'admin').map((item) => (
                        <NavLink
                            key={item.href}
                            to={item.href}
                            className={({ isActive }) =>
                                `group flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${isActive
                                    ? 'bg-accent-500/15 text-accent-400 border border-accent-500/25'
                                    : 'text-slate-400 hover:text-slate-200 hover:bg-white/5'
                                }`
                            }
                        >
                            <span className="flex-shrink-0">{item.icon}</span>
                            {sidebarOpen && (
                                <span className="truncate">{item.label}</span>
                            )}
                            {!sidebarOpen && (
                                <span className="absolute left-[72px] px-2 py-1 text-xs bg-[#0d1b3e] border border-white/10 rounded-lg
                                    opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap transition-opacity z-50 shadow-lg">
                                    {item.label}
                                </span>
                            )}
                        </NavLink>
                    ))}
                </nav>

                {/* User section */}
                <div className="border-t border-white/5 p-3">
                    {sidebarOpen ? (
                        <div className="flex items-center gap-3 px-2 py-2 rounded-xl hover:bg-white/5 transition-colors">
                            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center text-xs font-bold text-white flex-shrink-0">
                                {avatarInitials}
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-xs font-semibold text-slate-200 truncate">{user?.name || 'Admin'}</p>
                                <p className="text-[10px] text-slate-500 truncate">{user?.email}</p>
                            </div>
                            <button onClick={handleLogout} title="Logout"
                                className="flex-shrink-0 text-slate-500 hover:text-red-400 transition-colors p-1">
                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                        d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                                </svg>
                            </button>
                        </div>
                    ) : (
                        <button onClick={handleLogout} title="Logout"
                            className="w-full flex items-center justify-center p-2 rounded-xl text-slate-500 hover:text-red-400 hover:bg-white/5 transition-colors">
                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                    d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                            </svg>
                        </button>
                    )}
                </div>

                {/* Collapse toggle (desktop) */}
                <button
                    onClick={() => setSidebarOpen(!sidebarOpen)}
                    className="hidden lg:flex items-center justify-center absolute -right-3 top-[72px] w-6 h-6 rounded-full bg-[#0d1b3e] border border-white/10 text-slate-400 hover:text-slate-200 hover:border-accent-500/50 transition-all shadow-lg"
                >
                    <svg className={`w-3 h-3 transition-transform duration-300 ${sidebarOpen ? '' : 'rotate-180'}`}
                        fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
                    </svg>
                </button>
            </aside>

            {/* ── Main content ─────────────────────────────────────── */}
            <div className={`flex-1 flex flex-col min-h-screen transition-all duration-300 ${sidebarOpen ? 'lg:ml-64' : 'lg:ml-[72px]'}`}>

                {/* Top bar */}
                <header className="sticky top-0 z-30 flex items-center gap-4 px-4 sm:px-6 h-16
                    bg-[#060d1f]/80 backdrop-blur-xl border-b border-white/5">
                    {/* Mobile hamburger */}
                    <button
                        onClick={() => setMobileSidebarOpen(true)}
                        className="lg:hidden text-slate-400 hover:text-slate-200 transition-colors"
                    >
                        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                        </svg>
                    </button>

                    <div className="flex-1">
                        <BreadcrumbBar pathname={location.pathname} />
                    </div>

                    <div className="flex items-center gap-3">
                        {/* Role badge */}
                        <span className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-accent-500/10 border border-accent-500/20 text-[11px] font-semibold text-accent-400 uppercase tracking-widest">
                            <span className="w-1.5 h-1.5 rounded-full bg-accent-400 animate-pulse" />
                            {user?.role || 'Admin'}
                        </span>
                        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center text-xs font-bold text-white">
                            {avatarInitials}
                        </div>
                    </div>
                </header>

                {/* Page content */}
                <main className="flex-1 p-4 sm:p-6 lg:p-8">
                    <Outlet />
                </main>
            </div>
        </div>
    );
}

function BreadcrumbBar({ pathname }) {
    const crumbs = pathname.replace('/adminpanel', '').split('/').filter(Boolean);
    const labels = { dashboard: 'Dashboard', models: 'Models', upload: 'Upload', list: 'List' };
    return (
        <nav className="flex items-center gap-1.5 text-sm">
            <span className="text-slate-500">Admin</span>
            {crumbs.map((c, i) => (
                <span key={i} className="flex items-center gap-1.5">
                    <svg className="w-3.5 h-3.5 text-slate-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                    <span className={i === crumbs.length - 1 ? 'text-slate-200 font-medium' : 'text-slate-500'}>
                        {labels[c] || c.charAt(0).toUpperCase() + c.slice(1)}
                    </span>
                </span>
            ))}
        </nav>
    );
}
